// src/components/UserModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Accordion, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Website, WebsiteAccess, Menu, User, FormError } from "../../PropTypes/UserManagementTypes";
import "../../Assets/scss/components/user-management.scss";

type Props = {
  show: boolean;
  onHide: () => void;
  websiteList: Website[];
  allPermission: Menu[];
  onSave: (payload: { name: string; email: string }, editingUserId?: number, websites?: WebsiteAccess[]) => Promise<any>;
  editingUser?: User | null;
};

const UserModal: React.FC<Props> = ({ show, onHide, websiteList, allPermission, onSave, editingUser }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "" });
  const [selectedWebsitesAccess, setSelectedWebsitesAccess] = useState<WebsiteAccess[]>([]);
  const [websiteSearch, setWebsiteSearch] = useState("");
  const [selectAllWebsites, setSelectAllWebsites] = useState(true);
  const [error, setError] = useState<FormError>({});

  useEffect(() => {
    if (show) {
      if (editingUser) {
        setForm({ name: editingUser.name, email: editingUser.email });
      } else {
        setForm({ name: "", email: "" });
      }
      // default access for new user: all websites with all permissions
      const defaultAccess = websiteList?.map((site) => ({
        website: site.domain,
        sidebarMenus: allPermission?.map((m) => m.name) || [],
      }));
      setSelectedWebsitesAccess(defaultAccess);
      setWebsiteSearch("");
      setSelectAllWebsites(true);
      setError({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editingUser, websiteList, allPermission]);

  const filteredWebsiteList = useMemo(
    () => websiteList.filter((site) => site.domain.toLowerCase().includes(websiteSearch.toLowerCase())),
    [websiteList, websiteSearch]
  );

  const validateForm = () => {
    const newErrors: FormError = {};
    if (!form.name || form.name.trim() === "") newErrors.name = t("Full name is required.");
    else if (!/^[A-Za-z\s]{1,50}$/.test(form.name)) newErrors.name = t("Name should not contain numbers or special characters.");

    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = t("Enter a valid email address.");

    if (!editingUser && selectedWebsitesAccess.length === 0) newErrors.websites = t("Select at least one website.");

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const websites = selectedWebsitesAccess.length > 0 ? selectedWebsitesAccess : undefined;
    await onSave({ name: form.name, email: form.email }, editingUser?.id, websites);
    onHide();
  };

  const updateSelectAllState = (updatedAccess: WebsiteAccess[]) => {
    setSelectAllWebsites(websiteList.length > 0 && updatedAccess.length === websiteList.length);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{editingUser ? t("Edit User") : t("Invite User")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row">
            <div className="col-md-6 mb-3">
              <Form.Label>{t("Full Name")}</Form.Label>
              <Form.Control type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              {error?.name && <small className="text-danger">{error?.name}</small>}
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>{t("Email")}</Form.Label>
              <Form.Control type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editingUser} />
              {error?.email && <small className="text-danger">{error?.email}</small>}
            </div>
          </div>

          {!editingUser && (
            <>
              <div className="d-flex justify-content-between align-items-center mt-3">
                <Form.Label className="ms-2">{t("Website Access & Permissions")}</Form.Label>
                <Form.Check type="checkbox" label={t("Select All Websites")} className="me-4 checkbox-sm" checked={selectAllWebsites}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSelectAllWebsites(checked);
                    if (checked) {
                      const allSites = websiteList.map((site) => ({ website: site.domain, sidebarMenus: allPermission.map((m) => m.name) }));
                      setSelectedWebsitesAccess(allSites);
                    } else {
                      setSelectedWebsitesAccess([]);
                    }
                  }}
                />
              </div>

              <div className="p-2 mb-1 mt-1">
                <Form.Control type="text" placeholder={t("Search Website...")} value={websiteSearch} onChange={(e) => setWebsiteSearch(e.target.value)} />
              </div>

              {error?.websites && <small className="text-danger ms-2">{error?.websites}</small>}

              <div style={{ maxHeight: "330px", overflowY: "auto", padding: "8px" }}>
                {filteredWebsiteList.length === 0 ? (
                  <div>{t("No Website found")}</div>
                ) : (
                  <Accordion alwaysOpen>
                    {filteredWebsiteList.map((site, idx) => {
                      const siteAccess = selectedWebsitesAccess.find((w) => w.website === site.domain) || { website: site.domain, sidebarMenus: [] };
                      const isWebsiteSelected = selectedWebsitesAccess.some((w) => w.website === site.domain);
                      return (
                        <Accordion.Item eventKey={String(idx)} key={site.id}>
                          <Accordion.Header>
                            <Form.Check
                              type="checkbox"
                              label={site.domain}
                              className="checkbox-sm"
                              checked={isWebsiteSelected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                let updated: WebsiteAccess[] = [];
                                if (e.target.checked) {
                                  updated = [...selectedWebsitesAccess, { website: site.domain, sidebarMenus: allPermission.map((m) => m.name) }];
                                } else {
                                  updated = selectedWebsitesAccess.filter((w) => w.website !== site.domain);
                                }
                                setSelectedWebsitesAccess(updated);
                                updateSelectAllState(updated);
                              }}
                            />
                          </Accordion.Header>
                          <Accordion.Body>
                            <hr className="my-2" />
                            {allPermission.map((menu) => {
                              const isChecked = siteAccess.sidebarMenus.includes(menu.name);
                              return (
                                <div key={menu.id} className="d-flex align-items-center mb-1" style={{ gap: "6px" }}>
                                  <input
                                    type="checkbox"
                                    id={`menu-${site.domain}-${menu.id}`}
                                    className="form-check-input checkbox-sm"
                                    checked={isChecked}
                                    style={{ fontSize: 11 }}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setSelectedWebsitesAccess((prev) => {
                                        const exists = prev.find((w) => w.website === site.domain);
                                        let updated: WebsiteAccess[];
                                        if (exists) {
                                          updated = prev.map((w) =>
                                            w.website === site.domain
                                              ? { ...w, sidebarMenus: checked ? [...w.sidebarMenus, menu.name] : w.sidebarMenus.filter((m) => m !== menu.name) }
                                              : w
                                          );
                                          updated = updated.filter((w) => w.sidebarMenus.length > 0);
                                        } else if (checked) {
                                          updated = [...prev, { website: site.domain, sidebarMenus: [menu.name] }];
                                        } else {
                                          updated = prev;
                                        }
                                        updateSelectAllState(updated);
                                        return updated;
                                      });
                                    }}
                                  />
                                  <label htmlFor={`menu-${site.domain}-${menu.id}`} className="form-check-label" onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }}>
                                    {menu.title}
                                  </label>
                                </div>
                              );
                            })}
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                )}
              </div>
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("Cancel")}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {editingUser ? t("Update") : t("Invite User")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserModal;
